// Root-level paths that belong to the AtadaBoard website / platform and must
// never be treated as a school public profile path.
const reservedPaths = new Set([
  'about',
  'admin',
  'admission',
  'api',
  'app',
  'assets',
  'billing',
  'blog',
  'card',
  'console',
  'contact',
  'help',
  'home',
  'login',
  'logout',
  'pricing',
  'privacy',
  'school',
  'schools',
  'settings',
  'signup',
  'static',
  'store',
  'student',
  'support',
  'terms',
  'www',
]);

// Paths this web app already serves, plus static files that live in /public.
const appPaths = [
  'download',
  'demo',
  'features',
  'testimonials',
  'apple-app-site-association',
  '.well-known',
  'favicon.ico',
  'index.html',
  'manifest.json',
  'robots.txt',
  'logo192.png',
  'logo512.png',
  'sitemap.xml',
  'asset-manifest.json',
];

appPaths.forEach((path) => reservedPaths.add(path));

// A school public path is a single lowercase slug: letters, numbers and dashes.
const PUBLIC_PATH_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$|^[a-z0-9]$/;

/**
 * Trim surrounding slashes / whitespace and lowercase a raw route value.
 * Returns an empty string when nothing usable is left.
 */
export const normalizePublicPath = (rawPath) => {
  if (typeof rawPath !== 'string') return '';

  let value = rawPath.trim();

  try {
    value = decodeURIComponent(value);
  } catch (error) {
    // Malformed escape sequences: keep the raw value, validation will reject it.
  }

  return value.replace(/^\/+/, '').replace(/\/+$/, '').trim().toLowerCase();
};

/** True when the normalized path is owned by the main website or platform. */
export const isReservedPath = (rawPath) => {
  const path = normalizePublicPath(rawPath);
  if (!path) return true;

  // Anything that looks like a file (favicon.ico, sw.js, ...) is never a school.
  if (path.includes('.')) return true;

  return reservedPaths.has(path);
};

/** True when the path could plausibly be a school public path. */
export const isValidPublicPath = (rawPath) => {
  const path = normalizePublicPath(rawPath);
  if (!path) return false;

  return PUBLIC_PATH_PATTERN.test(path);
};

/**
 * True when we should ask the API for a school profile for this path.
 * Guards the network call against reserved and clearly malformed paths.
 */
export const isSchoolProfileCandidate = (rawPath) =>
  !isReservedPath(rawPath) && isValidPublicPath(rawPath);

export { reservedPaths };
